import { LineImageAlt } from '@qr-front/icons/line-image-alt'
import { LineImagePlus } from '@qr-front/icons/line-image-plus'
import { TFileUploadHandle } from '@qr-front/ui/components/FileUpload'
import { fieldLabelCls } from '@qr-front/ui/components/InputField'
import { SmartScrollContainer } from '@qr-front/ui/components/SmartScrollContainer'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

import { SocialPlatformButton } from '@/components/SocialPlatformButton'
import { TUploadedMedia } from '@/services'
import { getStore } from '@/store'

import { FileUploadToMediaStorage } from '../../../../components/FileUploadToMediaStorage'
import { TGeneratorFormSection } from '../../components/GeneratorFormSection'
import { SOCIAL_PLATFORM_LIST } from '../../utils/socialPlatformsConfig'

// 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024

export type TSocialPlatformId = (typeof SOCIAL_PLATFORM_LIST)[number]['id']

const orderedPlatformIdList: TSocialPlatformId[] = [
	'twitter',
	'x',
	'youtube',
	'instagram',
	'tiktok',
	'linkedin',
	'pinterest',
	'outlook',
	'apple',
	'gmail',
	'whatsapp',
	'facebook',
	'netflix',
]

const ICON_TO_SVG_URL: Partial<Record<TSocialPlatformId, string>> = {
	facebook: '/icons/socials/Facebook.svg',
	instagram: '/icons/socials/Instagram.svg',
	twitter: '/icons/socials/Twitter.svg',
	x: '/icons/socials/X.svg',
	whatsapp: '/icons/socials/Whatsapp.svg',
	tiktok: '/icons/socials/TikTok.svg',
	youtube: '/icons/socials/Youtube.svg',
	pinterest: '/icons/socials/Pinterest.svg',
	linkedin: '/icons/socials/Linkedin.svg',
	outlook: '/icons/socials/Outlook.svg',
	apple: '/icons/socials/Apple.svg',
	gmail: '/icons/socials/Gmail.svg',
	netflix: '/icons/socials/Netflix.svg',
}

const sortedSocialIconList = SOCIAL_PLATFORM_LIST.filter(({ id }) =>
	orderedPlatformIdList.includes(id),
).sort((a, b) => {
	const indexA = orderedPlatformIdList.indexOf(a.id)
	const indexB = orderedPlatformIdList.indexOf(b.id)
	return indexA - indexB
})

export const qrSelectedLogoUrlAtom = atomWithReset<string | null>(null)
export const qrLogoImageAtom = atomWithReset<TUploadedMedia | null>(null)
export const qrLogoImageFileAtom = atomWithReset<File | null>(null)

export const LogoUploadField = () => {
	const t = useTranslations()

	const setSelectedLogoUrl = useSetAtom(qrSelectedLogoUrlAtom)
	const setLogoImage = useSetAtom(qrLogoImageAtom)
	const [file, setFile] = useAtom(qrLogoImageFileAtom)
	const fileUploadTranslations = {
		title: t('ui__file_upload__upload_image'),
		caption: t('ui__file_upload__max_image_size', {
			size: MAX_FILE_SIZE / 1024 / 1024,
		}),
		deleteButtonLabel: t('ui__file_upload__delete_button'),
		unsupportedFormatErrorMessage: t(
			'ui__file_upload__unsupported_format_error',
		),
		sizeTooLargeErrorMessage: t('ui__file_upload__file_size_error', {
			maxSize: MAX_FILE_SIZE / 1024 / 1024,
		}),
	}

	const cropDialogTranslations = {
		zoom: t('ui__image_crop_dialog__zoom'),
		closeButton: t('ui__popup__close_button'),
		saveButton: t('ui__image_crop_dialog__save_button'),
	}

	const fileUploadRef = useRef<TFileUploadHandle>(null)

	// Reset file upload error when predefined logo is selected
	useEffect(() => {
		const unsubscribe = getStore().sub(qrSelectedLogoUrlAtom, () => {
			if (getStore().get(qrSelectedLogoUrlAtom) !== null) {
				fileUploadRef.current?.clearError()
			}
		})
		return unsubscribe
	}, [])

	return (
		<div>
			<label className={fieldLabelCls}>
				{t('generator__design_form__logo_section__upload_label')}
			</label>
			<FileUploadToMediaStorage
				uploadPurpose="qr_logo"
				ref={fileUploadRef}
				initialFileList={file ? [file] : []}
				isLogoUpload
				onUploadStart={(incoming) => {
					setSelectedLogoUrl(null)
					setFile(incoming)
				}}
				onUploadComplete={(uploaded, original) => {
					setSelectedLogoUrl(null)
					setFile(original)
					setLogoImage(uploaded)
				}}
				onDelete={() => {
					setFile(null)
					setLogoImage(null)
				}}
				maxFileSize={MAX_FILE_SIZE}
				placeholderIcon={<LineImageAlt />}
				translations={fileUploadTranslations}
				showCropDialog={false}
				resizeResolution={{ width: 500, height: 500 }}
				cropDialogTranslations={cropDialogTranslations}
				className="mt-2"
			/>
		</div>
	)
}

export const LogoDesignForm = () => {
	const t = useTranslations()

	const [selectedLogoUrl, setSelectedLogoUrl] = useAtom(qrSelectedLogoUrlAtom)

	const buttonList = sortedSocialIconList.map(({ id, name, Icon }) => {
		return (
			<SocialPlatformButton
				key={id}
				name={name}
				Icon={Icon}
				isSelected={selectedLogoUrl === ICON_TO_SVG_URL[id]}
				onClick={() => {
					const logoUrl = ICON_TO_SVG_URL[id]
					if (logoUrl) {
						setSelectedLogoUrl(
							logoUrl === selectedLogoUrl ? null : logoUrl,
						)
					}
				}}
			/>
		)
	})

	return (
		<div className="flex flex-col gap-6 tablet:gap-8">
			<div>
				<label className={fieldLabelCls}>
					{t('generator__design_form__logo_section__select_logo')}
				</label>
				<SmartScrollContainer className="mt-2">
					<ul className="flex gap-2 tablet:gap-4">{buttonList}</ul>
				</SmartScrollContainer>
			</div>
			<LogoUploadField />
		</div>
	)
}

export const useLogoDesignForm = (): TGeneratorFormSection => {
	const t = useTranslations()

	return {
		id: 'logo',
		title: t('generator__design_form__logo_section__title'),
		description: t(
			'generator__design_form__logo_section__description__changed',
		),
		// TODO: Temporary key. After deleting the old `...__description` from Lokalise, rename this __changed key back to `...__description`.
		icon: <LineImagePlus />,
		Content: LogoDesignForm,
	}
}
